import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Tooltip
} from '@mui/material';
import { authenticatedFetch } from '../utils/api';
import { useDashboardWebSocket } from '../contexts/DashboardWebSocketContext';
import { useDashboardLive } from '../contexts/DashboardLiveContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import LoginIcon from '@mui/icons-material/Login';
import DoorFrontIcon from '@mui/icons-material/DoorFront';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import StorageIcon from '@mui/icons-material/Storage';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import BadgeIcon from '@mui/icons-material/Badge';
import NoAccountsIcon from '@mui/icons-material/NoAccounts';

const ATTENDANCE_SUMMARY_WS_FALLBACK_MS = 8000;

const ATTENDANCE_MESSAGE_TYPES = new Set([
  'today_attendance_snapshot',
  'today_attendance_update',
]);

// Metrics with Icons
const metrics = [
  { key: 'total_in', label: 'Present/Total', accent: '#10b981', icon: LoginIcon },
  { key: 'in_premises', label: 'In premises', accent: '#f59e0b', icon: DoorFrontIcon },
  { key: 'out_premises', label: 'Out premises', accent: '#ef4444', icon: MeetingRoomIcon },
  { key: 'late_entries', label: 'Late entries', accent: '#ef4444', icon: AccessTimeIcon },
  { key: 'ssd_storage', label: 'SSD storage', accent: '#10b981', icon: StorageIcon },
];

const roleMetrics = [
  { key: 'vendor', label: 'Vendor', accent: '#6366f1', icon: AssignmentIndIcon, format: 'present_total' },
  { key: 'visitor', label: 'Visitor', accent: '#8b5cf6', icon: BadgeIcon, format: 'present_total' },
  { key: 'intrusion', label: 'Intrusion', accent: '#40518A', icon: NoAccountsIcon, format: 'count' },
];

const METRIC_DISPLAY_ORDER = [
  'total_in',
  'vendor',
  'visitor',
  'intrusion',
  'in_premises',
  'out_premises',
  'late_entries',
  'ssd_storage',
];

const AttendanceSummaryCard = ({ raw }) => {
  const navigate = useNavigate();
  const { permissions } = useAuth();
  const { subscribe, getReadyState } = useDashboardWebSocket();
  const { isLiveEnabled, liveVersion } = useDashboardLive();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ssdStorage, setSsdStorage] = useState(null);
  const [recordingsStorage, setRecordingsStorage] = useState(null);
  const [junctionTemperatureC, setJunctionTemperatureC] = useState(null);
  const [companyStats, setCompanyStats] = useState([]);
  const usedRestFallbackRef = useRef(false);
  const hasSummaryRef = useRef(false);
  const fallbackTimerRef = useRef(null);
  const ssdFetchStartedRef = useRef(false);

  const vendorManagementEnabled = permissions?.feature_flags?.vendor_management === true;
  const visitorManagementEnabled = permissions?.feature_flags?.visitor_management === true;
  const intrusionRouteAllowed = Array.isArray(permissions?.allowed_routes)
    && permissions.allowed_routes.includes('/intrusion');

  const getStorageColor = (percent) => {
    if (typeof percent !== 'number') return '#64748b';
    if (percent < 50) return '#16a34a';
    if (percent < 75) return '#ca8a04';
    if (percent < 85) return '#ea580c';
    return '#dc2626';
  };

  const applySummaryPayload = (payload) => {
    if (!payload || payload.status !== 'success') {
      return;
    }
    hasSummaryRef.current = true;
    setData({
      status: payload.status,
      date: payload.date,
      summary: payload.summary || {},
    });
    setError(null);
    setLoading(false);
  };

  const clearFallbackTimer = () => {
    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
  };

  const fetchSummaryRest = async () => {
    clearFallbackTimer();
    try {
      const response = await authenticatedFetch('/dashboard/today-attendance-summary');
      const result = await response.json();
      applySummaryPayload(result);
    } catch (err) {
      console.error('[Dashboard] REST attendance summary failed:', err);
      setError(err.message || 'Unable to load attendance summary');
      setLoading(false);
    }
  };

  const fetchSummaryRestFallback = async () => {
    if (usedRestFallbackRef.current || hasSummaryRef.current) {
      return;
    }

    const readyState = getReadyState();
    if (readyState === WebSocket.CONNECTING || readyState === WebSocket.OPEN) {
      return;
    }

    usedRestFallbackRef.current = true;
    clearFallbackTimer();
    try {
      const response = await authenticatedFetch('/dashboard/today-attendance-summary');
      const result = await response.json();
      applySummaryPayload(result);
    } catch (err) {
      console.error('[Dashboard WS] REST fallback failed:', err);
      setError(err.message || 'Unable to load attendance summary');
      setLoading(false);
    }
  };

  const scheduleRestFallback = () => {
    clearFallbackTimer();
    fallbackTimerRef.current = setTimeout(() => {
      fallbackTimerRef.current = null;
      if (hasSummaryRef.current || usedRestFallbackRef.current) {
        return;
      }
      const readyState = getReadyState();
      if (readyState === WebSocket.OPEN) {
        return;
      }
      fetchSummaryRestFallback();
    }, ATTENDANCE_SUMMARY_WS_FALLBACK_MS);
  };

  // Today-attendance summary via WebSocket when live, otherwise REST API.
  useEffect(() => {
    hasSummaryRef.current = false;
    usedRestFallbackRef.current = false;
    setLoading(true);
    setError(null);

    if (!isLiveEnabled) {
      fetchSummaryRest();
      return undefined;
    }

    const unsubscribe = subscribe((payload) => {
      if (!ATTENDANCE_MESSAGE_TYPES.has(payload?.type)) {
        return;
      }
      hasSummaryRef.current = true;
      clearFallbackTimer();
      applySummaryPayload(payload);
    });

    scheduleRestFallback();

    return () => {
      clearFallbackTimer();
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLiveEnabled, liveVersion]);

  useEffect(() => {
    const fetchCompanyStats = async () => {
      try {
        const response = await authenticatedFetch('/dashboard/company-wise-employee-counts');
        const result = await response.json();
        if (result.status === 'success') {
          setCompanyStats(result.data || []);
        }
      } catch (err) {
        console.error('Failed to fetch company stats:', err);
      }
    };

    fetchCompanyStats();
  }, []);

  useEffect(() => {
    if (!data || ssdFetchStartedRef.current) {
      return undefined;
    }
    ssdFetchStartedRef.current = true;

    const fetchSsdStorage = async () => {
      try {
        const response = await authenticatedFetch('/dashboard/ssd-storage');
        const result = await response.json();
        if (result.status === 'success') {
          if (result.storage) {
            setSsdStorage(result.storage);
          }
          setRecordingsStorage(result.recordings || null);
          const junctionTemp =
            typeof result.junction_temperature_c === 'number'
              ? result.junction_temperature_c
              : typeof result.storage?.junction_temperature_c === 'number'
                ? result.storage.junction_temperature_c
                : null;
          setJunctionTemperatureC(junctionTemp);
        }
      } catch (err) {
        console.error('Failed to fetch SSD storage:', err);
      }
    };

    let idleId = null;
    let timeoutId = null;
    if (typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(fetchSsdStorage, { timeout: 3000 });
    } else {
      timeoutId = window.setTimeout(fetchSsdStorage, 1500);
    }

    return () => {
      if (idleId !== null) {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [data]);

  const summary = useMemo(() => data?.summary || {}, [data]);
  const showLateEntriesTile = summary.late_entry_tracking_enabled !== false;

  const visibleRoleMetrics = useMemo(
    () => roleMetrics.filter((metric) => {
      if (metric.key === 'vendor') return vendorManagementEnabled;
      if (metric.key === 'visitor') return visitorManagementEnabled;
      if (metric.key === 'intrusion') return intrusionRouteAllowed;
      return true;
    }),
    [vendorManagementEnabled, visitorManagementEnabled, intrusionRouteAllowed],
  );

  const visibleMetrics = useMemo(
    () => metrics.filter((metric) => metric.key !== 'late_entries' || showLateEntriesTile),
    [showLateEntriesTile],
  );

  const allVisibleMetrics = useMemo(() => {
    const metricsByKey = new Map();
    visibleMetrics.forEach((metric) => metricsByKey.set(metric.key, metric));
    visibleRoleMetrics.forEach((metric) => metricsByKey.set(metric.key, metric));
    return METRIC_DISPLAY_ORDER
      .map((key) => metricsByKey.get(key))
      .filter(Boolean);
  }, [visibleMetrics, visibleRoleMetrics]);

  const statsToShow = companyStats;
  const statsLabel = 'Employees by Company';
  const statsKey = 'company_name';
  const currentSummary = summary;
  const totalKey = 'total_unique_employees';

  const getMetricValue = (metric) => {
    if (metric.key === 'ssd_storage') {
      const storagePercent = ssdStorage?.use_percent;
      return typeof storagePercent === 'number' ? `${storagePercent}%` : '--';
    }
    if (metric.key === 'total_in') {
      return `${Number(currentSummary.total_in || 0).toLocaleString()}/${Number(currentSummary[totalKey] || 0).toLocaleString()}`;
    }
    if (metric.key === 'vendor') {
      return `${Number(currentSummary.vendor_in || 0).toLocaleString()}/${Number(currentSummary.vendor_total || 0).toLocaleString()}`;
    }
    if (metric.key === 'visitor') {
      return `${Number(currentSummary.visitor_in || 0).toLocaleString()}/${Number(currentSummary.visitor_total || 0).toLocaleString()}`;
    }
    if (metric.key === 'intrusion') {
      return Number(currentSummary.intrusion_count || 0).toLocaleString();
    }
    return Number(currentSummary[metric.key] || 0).toLocaleString();
  };

  const handleMetricClick = (metric) => {
    if (metric.key === 'ssd_storage') {
      return;
    }

    const today = data?.date || new Date().toISOString().slice(0, 10);

    if (metric.key === 'vendor') {
      navigate(`/present?start_date=${today}&end_date=${today}&employee_type=vendor`);
      return;
    }
    if (metric.key === 'visitor') {
      navigate(`/present?start_date=${today}&end_date=${today}&employee_type=visitor`);
      return;
    }
    if (metric.key === 'intrusion') {
      navigate('/intrusion');
      return;
    }

    navigate('/present', { state: { attendanceFilter: metric.key, timestamp: Date.now() } });
  };

  const metricsGridSx = {
    display: 'grid',
    gridTemplateColumns: {
      xs: '1fr',
      sm: 'repeat(2, 1fr)',
      md: 'repeat(4, 1fr)',
      lg: 'repeat(4, 1fr)',
    },
    gap: 2,
  };

  const renderMetricCard = (metric) => {
    const IconComponent = metric.icon;
    const isStorageCard = metric.key === 'ssd_storage';
    const storagePercent = ssdStorage?.use_percent;
    const displayAccent = isStorageCard ? getStorageColor(storagePercent) : metric.accent;

    const tooltipContent = isStorageCard && ssdStorage ? (
      <Box sx={{ p: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#333', borderBottom: '1px solid #eee', pb: 1 }}>
          SSD Details
        </Typography>
        <Typography variant="body2" sx={{ py: 0.5, color: '#333', fontWeight: 600 }}>
          Filesystem: {ssdStorage.filesystem || '--'}
        </Typography>
        <Typography variant="body2" sx={{ py: 0.5, color: '#333', fontWeight: 600 }}>
          Type: {ssdStorage.type || '--'}
        </Typography>
        <Typography variant="body2" sx={{ py: 0.5, color: '#333', fontWeight: 600 }}>
          Size: {ssdStorage.size || '--'}
        </Typography>
        <Typography variant="body2" sx={{ py: 0.5, color: '#333', fontWeight: 600 }}>
          Used: {ssdStorage.used || '--'}
        </Typography>
        <Typography variant="body2" sx={{ py: 0.5, color: '#333', fontWeight: 600 }}>
          Available: {ssdStorage.avail || '--'}
        </Typography>
        <Typography variant="body2" sx={{ py: 0.5, color: '#333', fontWeight: 600 }}>
          Mounted on: {ssdStorage.mounted_on || '--'}
        </Typography>
        <Typography variant="body2" sx={{ py: 0.5, color: '#333', fontWeight: 600 }}>
          Temperature:{' '}
          {typeof (ssdStorage.junction_temperature_c ?? junctionTemperatureC) === 'number'
            ? `${ssdStorage.junction_temperature_c ?? junctionTemperatureC}°C`
            : '--'}
        </Typography>
        <Typography variant="body2" sx={{ py: 0.5, color: '#333', fontWeight: 600 }}>
          Recording files: {recordingsStorage?.size || '--'}
        </Typography>
      </Box>
    ) : metric.key === 'total_in' && statsToShow.length > 0 ? (
      <Box sx={{ p: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#333', borderBottom: '1px solid #eee', pb: 1 }}>
          {statsLabel}
        </Typography>
        {statsToShow.map((stat, index) => (
          <Typography
            key={index}
            variant="body2"
            sx={{ py: 0.5, color: '#333', fontWeight: 600 }}
          >
            {stat[statsKey]} - {stat.present_count}/{stat.total_count}
          </Typography>
        ))}
      </Box>
    ) : '';

    const cardContent = (
      <Box
        key={metric.key}
        onClick={() => handleMetricClick(metric)}
        sx={{
          cursor: isStorageCard ? 'default' : 'pointer',
          transition: 'transform 0.2s',
          '&:hover': {
            transform: isStorageCard ? 'none' : 'translateY(-4px)'
          }
        }}
      >
        <Box
          sx={{
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            p: 2,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            backgroundColor: 'background.paper',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            '&:hover': {
              boxShadow: '0 4px 16px rgba(0,0,0,0.12)'
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconComponent sx={{ color: displayAccent, fontSize: 24 }} />
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 500 }}>
              {metric.label}
            </Typography>
          </Box>

          <Typography
            variant="h5"
            sx={{ fontWeight: 600, color: displayAccent, mt: 1 }}
          >
            {getMetricValue(metric)}
          </Typography>
        </Box>
      </Box>
    );

    if (tooltipContent) {
      return (
        <Tooltip
          key={metric.key}
          title={tooltipContent}
          arrow
          placement="top"
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: 'rgba(255, 255, 255, 0.98)',
                border: '1px solid #e0e0e0',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                '& .MuiTooltip-arrow': {
                  color: 'rgba(255, 255, 255, 0.98)',
                  '&::before': {
                    border: '1px solid #e0e0e0'
                  }
                },
                maxWidth: 320,
                fontSize: '0.875rem',
                borderRadius: '8px',
                padding: '12px 16px',
              }
            }
          }}
        >
          {cardContent}
        </Tooltip>
      );
    }

    return cardContent;
  };

  return (
    <Card sx={{ 
      maxWidth: "100%",
          width: "100%",
          height: "100%",
          background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
          transition: "all 0.3s ease-in-out",
          border: "1px solid rgba(64, 81, 138, 0.12)",
          borderRadius: 3,
          boxShadow: "0 10px 28px rgba(31, 45, 84, 0.12)",
          "&:hover": { boxShadow: "0 12px 32px rgba(0,0,0,0.18)" },
          overflow: "visible",
     }}>
      <CardContent>

        {/* Title */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 2}}>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            Today's Attendance
          </Typography>
        </Box>

        {/* Loading spinner */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {/* Error message */}
        {error && !loading && <Alert severity="error">{error}</Alert>}

        {/* Metrics grid */}
        {!loading && !error && (
          <Box sx={metricsGridSx}>
            {allVisibleMetrics.map(renderMetricCard)}
          </Box>
        )}

        {/* Raw debug */}
        {raw}
      </CardContent>
    </Card>
  );
};

export default AttendanceSummaryCard;
